import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ShippingCard, type ShippingItem } from "@/components/ShippingCard";
import { useRegion } from "@/contexts/RegionContext";
import { useZone } from "@/contexts/ZoneContext";
import { useLocation as useLocationContext } from "@/contexts/LocationContext";
import { calculateDistance } from "@/lib/geolocation";
import { regions, australianCities } from "@shared/schema";
import { Truck, MapPin, Package } from "lucide-react";

// Phase 3.5: 서비스 타입
const serviceTypes = [
  { id: "all", label: "전체" },
  { id: "sea", label: "해상" },
  { id: "air", label: "항공" },
  { id: "consolidated", label: "합배송" },
  { id: "storage", label: "보관" },
  { id: "moving", label: "포장이사" },
];

export default function Shipping() {
  const { user } = useAuth();
  const { selectedRegion, selectedSuburb } = useRegion();
  const { selectedZoneId } = useZone();
  const { userLocation } = useLocationContext();
  const [selectedState, setSelectedState] = useState<string>(selectedRegion || "ALL");
  const [selectedCity, setSelectedCity] = useState<string>("ALL");
  const [searchTerm, setSearchTerm] = useState<string>("");
  // Phase 3.5: 택배/이사 전용 필터
  const [selectedServiceType, setSelectedServiceType] = useState<string>("all");
  const [minPrice, setMinPrice] = useState<string>("");
  const [maxPrice, setMaxPrice] = useState<string>("");

  // Phase 3.5: 택배/이사 목록 조회 (category: 'shipping' 필터)
  const { data: listings, isLoading } = useQuery<ShippingItem[]>({
    queryKey: ["/api/unified-market", selectedZoneId, selectedState, selectedCity, "shipping"],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append("category", "shipping");
      params.append("type", "ad");
      params.append("zone", selectedZoneId);
      if (selectedState !== "ALL") params.append("state", selectedState);
      if (selectedCity !== "ALL") params.append("city", selectedCity);

      const res = await fetch(`/api/unified-market?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to fetch listings");
      const data = await res.json();
      // 응답이 { ok: true, data: { items: [...] } } 형식일 수 있음
      if (data?.ok && data?.data?.items) {
        return data.data.items;
      }
      if (Array.isArray(data)) {
        return data;
      }
      return [];
    },
  });

  // Phase 3.5: 거리 계산 및 고급 필터링 로직
  const filteredListings = useMemo(() => {
    if (!listings) return [];

    // 거리 계산
    let result = listings.map((listing) => {
      let distance: number | undefined = undefined;
      if (
        userLocation &&
        listing.latitude &&
        listing.longitude &&
        typeof listing.latitude === "number" &&
        typeof listing.longitude === "number"
      ) {
        distance = calculateDistance(
          userLocation.latitude,
          userLocation.longitude,
          listing.latitude,
          listing.longitude
        );
      }
      return { ...listing, distance };
    });

    // 필터링
    result = result.filter((listing) => {
      // 검색어 필터
      if (
        searchTerm &&
        !listing.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !listing.description?.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        return false;
      }

      // 서비스 타입 필터
      if (selectedServiceType !== "all" && listing.serviceType !== selectedServiceType) {
        return false;
      }

      // 가격 필터
      if (minPrice || maxPrice) {
        const price = listing.priceCents;
        if (price === null || price === undefined || price === -1 || price === 0) {
          // 가격협의는 필터에서 제외하지 않음
        } else {
          const priceInDollars = price / 100;
          if (minPrice && priceInDollars < parseFloat(minPrice)) return false;
          if (maxPrice && priceInDollars > parseFloat(maxPrice)) return false;
        }
      }

      return true;
    });

    // 거리순 정렬 (거리가 있는 경우)
    return result.sort((a, b) => {
      if (a.distance !== undefined && b.distance !== undefined) {
        return a.distance - b.distance;
      }
      if (a.distance !== undefined) return -1;
      if (b.distance !== undefined) return 1;
      // 최신순 정렬
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [listings, searchTerm, selectedServiceType, minPrice, maxPrice, userLocation]);

  const stateRegion = regions.find((r) => r.id === selectedState);
  const availableCities =
    selectedState !== "ALL" && selectedState in australianCities
      ? australianCities[selectedState as keyof typeof australianCities]
      : [];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="bg-primary/5 border-b">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl font-bold mb-3" data-testid="text-title">
              택배/이사
            </h1>
            <p className="text-lg text-muted-foreground mb-6">
              해상/항공, 합배송, 보관, 포장이사 - 호주 한인 커뮤니티 택배 및 이사 서비스
            </p>
            <div className="flex gap-4 justify-center items-center flex-wrap">
              <Badge variant="secondary" className="gap-1">
                <Truck className="w-3 h-3" />
                택배/이사
              </Badge>
              <Badge variant="secondary" className="gap-1">
                <MapPin className="w-3 h-3" />
                지역기반
              </Badge>
              <Badge variant="secondary" className="gap-1">
                <Package className="w-3 h-3" />
                후기 기반
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Filters */}
          <aside className="lg:col-span-1">
            <Card>
              <CardContent className="p-4 space-y-4">
                {/* Search */}
                <div>
                  <label className="text-sm font-medium mb-2 block">검색</label>
                  <Input
                    placeholder="제목 또는 설명 검색..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    data-testid="input-search"
                  />
                </div>

                {/* State Filter */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    주/지역
                  </label>
                  <Select
                    value={selectedState}
                    onValueChange={(value) => {
                      setSelectedState(value);
                      setSelectedCity("ALL");
                    }}
                  >
                    <SelectTrigger data-testid="select-state">
                      <SelectValue placeholder="주 선택" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">전체 지역</SelectItem>
                      {regions
                        .filter((r) => r.id !== "ALL")
                        .map((region) => (
                          <SelectItem key={region.id} value={region.id}>
                            {region.label}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* City Filter */}
                {availableCities.length > 0 && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      도시
                    </label>
                    <Select
                      value={selectedCity}
                      onValueChange={setSelectedCity}
                    >
                      <SelectTrigger data-testid="select-city">
                        <SelectValue placeholder="도시 선택" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">전체 도시</SelectItem>
                        {availableCities.map((city) => (
                          <SelectItem key={city.id} value={city.id}>
                            {city.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Phase 3.5: 서비스 타입 필터 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    서비스 타입
                  </label>
                  <Select value={selectedServiceType} onValueChange={setSelectedServiceType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Phase 3.5: 가격 필터 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    가격 범위
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="최소"
                      value={minPrice}
                      onChange={(e) => setMinPrice(e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      placeholder="최대"
                      value={maxPrice}
                      onChange={(e) => setMaxPrice(e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>

                {/* Post Button */}
                {user && (
                  <Link href="/shipping/register">
                    <Button className="w-full" data-testid="button-register">
                      택배/이사 등록
                    </Button>
                  </Link>
                )}
                {!user && (
                  <Button className="w-full" variant="outline" disabled>
                    로그인 후 등록 가능
                  </Button>
                )}
              </CardContent>
            </Card>
          </aside>

          {/* Main Content - Listings */}
          <div className="lg:col-span-3">
            {/* Results Count */}
            <div className="mb-4 flex justify-between items-center">
              <p
                className="text-sm text-muted-foreground"
                data-testid="text-count"
              >
                {filteredListings
                  ? `${filteredListings.length}개의 서비스`
                  : "로딩 중..."}
              </p>
            </div>

            {/* Listings Grid */}
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <Card key={i}>
                    <Skeleton className="h-48 w-full" />
                    <CardContent className="p-4">
                      <Skeleton className="h-4 w-3/4 mb-2" />
                      <Skeleton className="h-4 w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : filteredListings && filteredListings.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredListings.map((listing) => (
                  <ShippingCard
                    key={listing.id}
                    item={listing}
                    onInquire={() => {
                      // TODO: 채팅 시작 로직
                      window.location.href = `/chat-start/shipping/${listing.id}`;
                    }}
                  />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-16 text-center">
                  <Truck className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
                  <p className="text-lg font-medium mb-2">
                    등록된 서비스가 없습니다
                  </p>
                  <p className="text-sm text-muted-foreground mb-6">
                    첫 번째 택배/이사 서비스를 등록해보세요!
                  </p>
                  {user && (
                    <Link href="/shipping/register">
                      <Button>택배/이사 등록하기</Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
